import { FlowOperationType } from '@activepieces/shared';
import { useReactFlow } from '@xyflow/react';
import { Plus } from 'lucide-react';
import React, { useState } from 'react';

import { useBuilderStateContext } from '../../builder-hooks';
import { CANVAS_PARAMETERS, flowUtilConsts } from '../utils/consts';
import { flowCanvasUtils } from '../utils/flow-canvas-utils';
import { ApButtonData } from '../utils/types';

import { AskAiIndicator, shouldShowAskAiIndicator } from './ask-ai-indicator';

import { PieceSelector } from '@/app/builder/pieces-selector';
import { PieceSelectorOperation } from '@/features/pieces/lib/types';
import { cn } from '@/lib/utils';

const ApAddButton = React.memo((props: ApButtonData) => {
  const [readonly, showAskAiIndicator] = useBuilderStateContext((state) => [
    state.readonly,
    shouldShowAskAiIndicator(state, props),
  ]);

  const [actionMenuOpen, setActionMenuOpen] = useState(false);

  const { fitView } = useReactFlow();

  if (readonly) {
    return null;
  }

  const operation = flowCanvasUtils.getOperationForAddButton(props) as Extract<
    PieceSelectorOperation,
    { type: FlowOperationType.ADD_ACTION }
  >;

  const focusBetweenNodes = () => {
    const connection = operation.connection;

    if (!connection?.sourceId || !connection?.targetId) return;

    fitView({
      nodes: [{ id: connection.sourceId }, { id: connection.targetId }],
      duration: CANVAS_PARAMETERS.animationDuration,
    });
  };

  const onPieceSelectorOpenChange = (open: boolean) => {
    if (open) focusBetweenNodes();

    const timeoutId = setTimeout(
      () => setActionMenuOpen(open),
      open ? CANVAS_PARAMETERS.animationDuration : 0,
    );

    return () => clearTimeout(timeoutId);
  };

  return (
    <PieceSelector
      operation={operation}
      open={actionMenuOpen}
      onOpenChange={onPieceSelectorOpenChange}
      asChild={true}
    >
      <div
        style={{
          width: flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.width + 'px',
          height: flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.height + 'px',
        }}
      >
        {showAskAiIndicator ? (
          <AskAiIndicator
            height={flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.height}
            width={flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.width}
          ></AskAiIndicator>
        ) : (
          <div
            style={{
              width: flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.width + 'px',
              height: flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.height + 'px',
            }}
            className={cn('rounded-xss cursor-pointer transition-all z-50', {
              'shadow-add-button': actionMenuOpen,
            })}
          >
            <div
              style={{
                width: flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.width + 'px',
                height: flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.height + 'px',
              }}
              className={cn(
                'bg-light-blue  relative group overflow-visible rounded-xss cursor-pointer  flex items-center justify-center  transition-all duration-300 ease-in-out',
                {
                  'bg-primary ': actionMenuOpen,
                },
              )}
            >
              {!actionMenuOpen && (
                <Plus className="w-3 h-3 stroke-[3px] text-white" />
              )}
            </div>
          </div>
        )}
      </div>
    </PieceSelector>
  );
});

ApAddButton.displayName = 'ApAddButton';
export { ApAddButton };
